import Add16 from '@carbon/icons-react/es/add/16';
import { Tooltip } from 'antd';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountStatus } from 'api/account';
import { PermissionSetType, User, UserStatus } from 'api/models';
import { PartnerCompany, PartnerUser } from 'api/partner/types';
import { Button } from 'components/atoms/button';
import { SearchInput } from 'components/atoms/input';
import { PageLoading } from 'components/atoms/loading';
import { FilterBar } from 'components/molecules/filter-bar';
import { ReactivationBillingWrapper } from 'components/organisms/module-settings/team-members/list/more-actions-menu/billing-wrapper';
import { TeamMemberTabs } from 'components/organisms/module-settings/team-members/tabs';
import { TeamMembersTabsEnum } from 'components/organisms/module-settings/team-members/types';
import { BodyShort01, ProductiveHeading06 } from 'components/styles';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useAccount, useBillingFeatureFlag } from 'hooks/use-account';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
import {
  useAvailableLicensesQuery,
  useReactivatePartnerMemberEstimateMutation,
} from 'queries/billing';
import { useAllPartnerCompanyMembers } from 'queries/partner';
import { AccountPermissionSetType } from 'stores/permission-sets';
import { fullName } from 'utils/full-name';
import { unreachable } from 'utils/unreachable';
import { TeamMemberDrawerModal } from '../../team-members/drawer-modal/drawer-modal';
import { PaymentStatus } from '../../team-members/manage-license';
import {
  ReactivateStatus,
  useResendInvitation,
  useUpdateTeamMemberStatus,
  InvitingStatus,
  useInviteAction,
} from './actions';
import { TeamMembersGrid } from './grid';
import {
  usePermissionOptions,
  useRolesOptionsForPartnerMembers,
  useTeamMemberDrawerStateManager,
} from './manage-team-members';

const FilterBarSecondary = styled(FilterBar.Secondary)`
  gap: var(--spacing-05);
`;
const FilterBarPrimary = styled(FilterBar.Primary)`
  gap: var(--spacing-05);
`;
const Value = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  ${ProductiveHeading06};
`;
const Text = styled.div`
  ${BodyShort01};
  color: var(--gray-50);
`;
const SeatsInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

interface Props {
  partner: PartnerCompany;
}

export const TeamMembersList: FC<Props> = ({ partner }) => {
  const { data: accountMeta } = useAccountMetaQuery();
  const { status: accountStatus } = useAccount();
  const isBillingEnabled = useBillingFeatureFlag();
  const translate = useTranslate();
  const currentUserPermissionSetType = useUserPermissionSetType();
  const [tab, setActiveTab] = useState(TeamMembersTabsEnum.Active);
  const [searchText, setSearchText] = useState('');
  const { data, isLoading: isPartnerUsersLoading } =
    useAllPartnerCompanyMembers(partner.companyId);

  const userStatusFilter = useCallback(
    ({ userStatus }: User) => {
      switch (tab) {
        case TeamMembersTabsEnum.Active:
          return userStatus === UserStatus.Active;
        case TeamMembersTabsEnum.Invited:
          return userStatus === UserStatus.Invited;
        case TeamMembersTabsEnum.Deactivated:
          return userStatus === UserStatus.Inactive;
        default:
          return unreachable(tab);
      }
    },
    [tab],
  );
  const partnerUsers: PartnerUser[] = useMemo(
    () =>
      (data?.members ?? [])
        .map(({ user, permissionSet }) => ({
          ...user,
          permissionSetName: permissionSet?.permissionSetName,
          permissionSetType:
            permissionSet?.permissionSetType as unknown as AccountPermissionSetType,
        }))
        .filter(userStatusFilter),
    [data?.members, userStatusFilter],
  );
  const filterFunction = useCallback(
    (user: PartnerUser) => {
      return fullName(user)
        .toLowerCase()
        .includes(searchText.trim().toLowerCase());
    },
    [searchText],
  );
  const onSearchInputChange = useCallback((event) => {
    setSearchText(event.target.value);
  }, []);

  const {
    deactivateUser,
    reactivateUser,
    beforeReactivateUser,
    onBillingFlowClose,
    reactivateStatus,
    userForReactivatingBillingFlow,
  } = useUpdateTeamMemberStatus();
  const { resendInvite } = useResendInvitation();
  const resendUserInvitation = useCallback(
    (user: PartnerUser) => {
      const member = data?.members.find(
        ({ user: { userId } }) => userId === user.userId,
      );

      if (member != null) {
        resendInvite(member);
      }
    },
    [data?.members, resendInvite],
  );
  const usedSeat = partner.usedSeat ?? 1;
  const maxSeat = partner.maxSeat ?? partner.usedSeat ?? 1;

  const {
    drawerState: teamMemberDrawerState,
    onClose,
    changeToEditMode,
    changeToInviteMode,
    changeToViewMode,
  } = useTeamMemberDrawerStateManager();
  const membersRef = useRef(data?.members);

  useEffect(() => {
    membersRef.current = data?.members;
  }, [data?.members]);

  const editUser = useCallback(
    (user) => {
      const member = membersRef.current?.find(
        ({ user: { userId } }) => userId === user.userId,
      );

      if (member != null) {
        changeToViewMode(member);
      }
    },
    [changeToViewMode],
  );
  const { permissionOptions } = usePermissionOptions();
  const { roleOptions } = useRolesOptionsForPartnerMembers();
  const {
    beforeInvite,
    invitingStatus,
    isEstimateLoading,
    invitePaymentStatus,
    onInvitePaymentStatusClosed,
  } = useInviteAction({
    partner,
    onInvited: onClose,
  });

  const searchPlaceholderText = useTranslation(
    'settings.team_members.search_placeholder',
  );
  const availableLicenses = useAvailableLicensesQuery();
  const inviteDisable =
    !partner.active ||
    usedSeat >= maxSeat ||
    (currentUserPermissionSetType === PermissionSetType.PartnerAdmin &&
      isBillingEnabled &&
      availableLicenses <= 0);
  const userCountValidator = useCallback(
    (_, values) => {
      if (
        values == null ||
        !isBillingEnabled ||
        teamMemberDrawerState.selected != null
      ) {
        return Promise.resolve();
      }
      const newMembersCount = values.length;
      const availableLicensesForPartner = partner.maxSeat - partner.usedSeat;

      if (currentUserPermissionSetType === PermissionSetType.PartnerAdmin) {
        if (newMembersCount > availableLicensesForPartner) {
          return Promise.reject(
            new Error(
              translate(
                'partners.team_members.invite.errors.partner_admin.no_license_for_partner',
                {
                  availableSeats: availableLicensesForPartner,
                  companyName: accountMeta?.company.companyName,
                },
              ),
            ),
          );
        }
        if (
          accountStatus !== AccountStatus.Trial &&
          newMembersCount > availableLicenses
        ) {
          return Promise.reject(
            new Error(
              translate(
                'partners.team_members.invite.errors.partner_admin.no_license_in_account',
                {
                  availableSeats: availableLicenses,
                  companyName: accountMeta?.company.companyName,
                },
              ),
            ),
          );
        }
      }

      if (currentUserPermissionSetType === PermissionSetType.AccountAdmin) {
        if (newMembersCount > availableLicensesForPartner) {
          return Promise.reject(
            new Error(
              translate(
                'partners.team_members.invite.errors.vendor_admin.no_license_for_partner',
                {
                  availableSeats: availableLicensesForPartner,
                },
              ),
            ),
          );
        }
      }

      return Promise.resolve();
    },
    [
      accountMeta?.company.companyName,
      accountStatus,
      availableLicenses,
      currentUserPermissionSetType,
      isBillingEnabled,
      partner.maxSeat,
      partner.usedSeat,
      teamMemberDrawerState.selected,
      translate,
    ],
  );

  return isPartnerUsersLoading ? (
    <PageLoading />
  ) : (
    <>
      <FilterBar>
        <FilterBarPrimary>
          <TeamMemberTabs
            tab={tab}
            onChangeTab={setActiveTab}
            members={data?.members ?? []}
          />
          <SeatsInfo>
            <FormattedMessage
              id="partners.team_members.seats_info.text"
              values={{
                count: partner.usedSeat ?? 0,
                totalSeats: partner.maxSeat ?? 0,
                b: (chunks) => <Value>{chunks}</Value>,
                text: (chunks) => <Text>{chunks}</Text>,
              }}
            />
          </SeatsInfo>
        </FilterBarPrimary>
        <FilterBarSecondary>
          <SearchInput
            direction="right"
            searchButtonProps={{ type: 'text' }}
            size="small"
            value={searchText}
            onChange={onSearchInputChange}
            placeholder={searchPlaceholderText}
          />
          <Tooltip
            title={
              inviteDisable ? (
                <FormattedMessage
                  id={
                    !partner.active
                      ? 'partners.team_members.invite.errors.partner_inactive'
                      : currentUserPermissionSetType ===
                        PermissionSetType.PartnerAdmin
                      ? 'partners.team_members.all_seats_taken.partner_admin.error'
                      : 'partners.team_members.all_seats_taken.vendor_admin.error'
                  }
                  values={{
                    companyName: accountMeta?.company.companyName,
                  }}
                />
              ) : null
            }
          >
            <Button
              type="primary"
              iconPlacement="right"
              icon={<Add16 />}
              size="small"
              onClick={changeToInviteMode}
              disabled={inviteDisable}
            >
              <FormattedMessage id="settings.team_members.invite_members_cta" />
            </Button>
          </Tooltip>
        </FilterBarSecondary>
      </FilterBar>
      <TeamMembersGrid
        users={partnerUsers}
        filterFunction={filterFunction}
        onEdit={editUser}
        onDeactivate={deactivateUser}
        onReactivate={beforeReactivateUser}
        onResendInvite={resendUserInvitation}
        currentTab={tab}
        partner={partner}
      />
      {teamMemberDrawerState?.visible ? (
        <TeamMemberDrawerModal
          drawerState={teamMemberDrawerState}
          onClose={onClose}
          showMemberDetail={changeToViewMode}
          changeToEditMode={changeToEditMode}
          onUserInvited={() => {}}
          onInviteSubmit={beforeInvite}
          processRequestInParent
          drawerSectionProps={{
            hideCapacitySection: true,
            hideFieldsSection: true,
            hideRoleCreation: true,
            hideTimesheetApproverSection: true,
          }}
          permissionOptions={permissionOptions}
          roleOptions={roleOptions}
          showLoader={
            invitingStatus === InvitingStatus.Inviting || isEstimateLoading
          }
          formInputRules={{
            email: [
              {
                validator: userCountValidator,
              },
            ],
          }}
          isVendorInvite={false}
        />
      ) : null}
      <PaymentStatus
        paymentStatus={invitePaymentStatus}
        onPaymentStatusClosed={onInvitePaymentStatusClosed}
      />
      {isBillingEnabled && userForReactivatingBillingFlow && (
        <ReactivationBillingWrapper
          visible={userForReactivatingBillingFlow != null}
          user={userForReactivatingBillingFlow}
          onSubmit={reactivateUser}
          isProcessing={
            reactivateStatus[userForReactivatingBillingFlow.userId] ===
            ReactivateStatus.Reactivating
          }
          isSuccess={
            reactivateStatus[userForReactivatingBillingFlow.userId] ===
            ReactivateStatus.Reactivated
          }
          isError={
            reactivateStatus[userForReactivatingBillingFlow.userId] ===
            ReactivateStatus.Failed
          }
          onCancel={onBillingFlowClose}
          estimateMutation={useReactivatePartnerMemberEstimateMutation}
        />
      )}
    </>
  );
};
